import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Select,
    MenuItem,
    Icon,
    InputLabel
} from "@mui/material";
import Swal from "sweetalert2";
import AptaTable from "examples/AptaTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDTypography from "components/MDTypography";
import axios from "axios";
import { SERVER_URL } from "constants/constant";

function AbstractCategories() {
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [formDetails, setFormDetails] = useState({ blogContent: "" });
    const [subFormDetails, setSubFormDetails] = useState({ title: "", parentCategory: "" });
    const [modalOpen, setModalOpen] = useState(false);
    const [subModalOpen, setSubModalOpen] = useState(false);
    const [modalType, setModalType] = useState("add");
    const [subModalType, setSubModalType] = useState("add");
    const [selectedCategory, setSelectedCategory] = useState("all"); // New state for filtering

    // Fetch all blog categories
    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${SERVER_URL}/categories`);
            setCategories(response.data);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    // Fetch subcategories
    const fetchSubCategories = async () => {
        try {
            const response = await axios.get(`${SERVER_URL}/subcategories`);
            setSubCategories(response.data);
        } catch (error) {
            console.error("Error fetching subcategories:", error);
        }
    };

    // Handle modal open for categories
    const handleOpenModal = (type, category = { blogContent: "" }) => {
        setModalType(type);
        setFormDetails({ ...category, blogContent: category?.catName });
        setModalOpen(true);
    };

    // Handle modal open for subcategories
    const handleOpenSubModal = (type, subCategory = { title: "", parentCategory: "" }) => {
        setSubModalType(type);
        setSubFormDetails({ ...subCategory, title: subCategory?.subCatName, parentCategory: subCategory?.subCatUnder?._id });
        setSubModalOpen(true);
    };

    // Handle modal close
    const handleCloseModal = () => setModalOpen(false);
    const handleCloseSubModal = () => setSubModalOpen(false);

    // Handle save category
    const handleSave = async (formDetails) => {
        const url =
            modalType === "add"
                ? `${SERVER_URL}/category`
                : `${SERVER_URL}/category/${formDetails._id}`;
        const method = modalType === "add" ? "POST" : "PUT";
        try {
            await axios({ method, url, data: { catName: formDetails.blogContent } });
            Swal.fire("Success", `Category ${modalType === "add" ? "created" : "updated"} successfully!`, "success");
            fetchCategories();
            setFormDetails({ blogContent: '' })
            handleCloseModal();
        } catch (error) {
            Swal.fire("Error", "Failed to save category.", "error");
            handleCloseModal();
        }
    };

    // Handle delete category
    const handleDelete = async (id) => {
        try {
            await axios.delete(`${SERVER_URL}/category/${id}`);
            Swal.fire("Success", "Category deleted successfully!", "success");
            fetchCategories();
        } catch (error) {
            Swal.fire("Error", "Failed to delete category.", "error");
            console.error("Error deleting category:", error);
        }
    };

    // Handle save subcategory
    const handleSaveSubCategory = async (subFormDetails) => {
        const url =
            subModalType === "add"
                ? `${SERVER_URL}/subcategory`
                : `${SERVER_URL}/subcategory/${subFormDetails._id}`;
        const method = subModalType === "add" ? "POST" : "PUT";

        try {
            await axios({
                method,
                url,
                data: {
                    subCatName: subFormDetails.title,
                    subCatUnder: subFormDetails.parentCategory,
                },
            });
            Swal.fire("Success", `Sub-category ${subModalType === "add" ? "created" : "updated"} successfully!`, "success");
            fetchSubCategories();
            setSubFormDetails({ title: "", parentCategory: "" })
            handleCloseSubModal();
        } catch (error) {
            handleCloseModal();
            Swal.fire("Error", "Failed to save sub-category.", "error");

            console.error("Error saving sub-category:", error);
        }
    };

    // Handle delete subcategory
    const handleDeleteSubCategory = async (id) => {
        try {
            await axios.delete(`${SERVER_URL}/subcategory/${id}`);
            Swal.fire("Success", "Sub-category deleted successfully!", "success");
            fetchSubCategories();
        } catch (error) {
            Swal.fire("Error", "Failed to delete sub-category.", "error");
            console.error("Error deleting sub-category:", error);
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchSubCategories();
    }, []);

    // Handle category filter change
    const handleCategoryFilterChange = (event) => {
        setSelectedCategory(event.target.value);
    };

    // Filter subcategories based on selected category
    const filteredSubCategories = selectedCategory === "all"
        ? subCategories
        : subCategories?.filter(sub => sub.subCatUnder?._id === selectedCategory);

    return (
        <DashboardLayout>
            <DashboardNavbar />

            {/* Categories Section */}
            <Box display="flex" mb={3} justifyContent="end">
                <Button
                    style={{ color: "white" }}
                    startIcon={<Icon>add</Icon>}
                    onClick={() => handleOpenModal("add", { blogContent: "" })}
                    variant="contained"
                >
                    Add Category
                </Button>
            </Box>
            <AptaTable
                columns={["TITLE", "ACTIONS"]}
                rows={categories.map((category) => ({
                    "TITLE": <MDTypography variant="button" fontWeight="medium">{category.catName}</MDTypography>,
                    ACTIONS: (
                        <ButtonGroup size="small">
                            <Button color="info" onClick={() => handleOpenModal("edit", category)}>
                                <Icon color="info">edit</Icon>
                            </Button>
                            <Button color="error" onClick={() => handleDelete(category._id)}>
                                <Icon color="error">delete</Icon>
                            </Button>
                        </ButtonGroup>
                    ),
                }))}
            />

            {/* Subcategories Section */}
            <Box display="flex" mb={3} mt={5} justifyContent="end">
                <Button
                    style={{ color: "white" }}
                    startIcon={<Icon>add</Icon>}
                    onClick={() => handleOpenSubModal("add", { title: "", parentCategory: "" })}
                    variant="contained"
                >
                    Add Subcategory
                </Button>
            </Box>
            <Box mb={3} mt={5} justifyContent="space-between">
                <InputLabel sx={{ paddingTop: 1 }}>Filter by Category</InputLabel>
                <Select
                    value={selectedCategory}
                    onChange={handleCategoryFilterChange}
                    fullWidth
                    style={{ width: 300, padding: 20, borderRadius: 10 }}
                >
                    <MenuItem value="all">All</MenuItem>
                    {categories.map((category) => (
                        <MenuItem key={category._id} value={category._id}>
                            {category.catName}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
            <AptaTable
                columns={["SUBCATEGORY TITLE", "CATEGORY", "ACTIONS"]}
                rows={filteredSubCategories?.map((sub) => ({
                    "SUBCATEGORY TITLE": <MDTypography variant="button" fontWeight="medium">{sub?.subCatName}</MDTypography>,
                    CATEGORY: categories.find(cat => cat._id === sub.subCatUnder?._id)?.catName || "Unknown",
                    ACTIONS: (
                        <ButtonGroup size="small">
                            <Button color="info" onClick={() => handleOpenSubModal("edit", sub)}>
                                <Icon color="info">edit</Icon>
                            </Button>
                            <Button color="error" onClick={() => handleDeleteSubCategory(sub._id)}>
                                <Icon color="error">delete</Icon>
                            </Button>
                        </ButtonGroup>
                    ),
                }))}
            />

            {/* Modals */}
            <BlogCategoryModal open={modalOpen} onClose={handleCloseModal} onSave={handleSave} details={formDetails} type={modalType} />
            <SubCategoryModal open={subModalOpen} onClose={handleCloseSubModal} onSave={handleSaveSubCategory} details={subFormDetails} type={subModalType} categories={categories} />
        </DashboardLayout>
    );
}

export default AbstractCategories;


const BlogCategoryModal = ({ open, onClose, onSave, details, type }) => {
    const [formData, setFormData] = useState({ blogContent: "" });

    useEffect(() => {
        if (details) {
            setFormData(details);
        }
    }, [details]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSave = () => {
        onSave(formData);
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{type === "add" ? "Add Category" : "Edit Category"}</DialogTitle>
            <DialogContent>
                <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
                    <TextField
                        label="Category Title"
                        name="blogContent"
                        value={formData.blogContent}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleSave} style={{ color: "white" }} color="primary" variant="contained">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const SubCategoryModal = ({ open, onClose, onSave, details, type, categories }) => {
    const [formData, setFormData] = useState({ title: "", parentCategory: "" });

    useEffect(() => {
        if (details) {
            setFormData(details);
        }
    }, [details]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSave = () => {
        onSave(formData);
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{type === "add" ? "Add Subcategory" : "Edit Subcategory"}</DialogTitle>
            <DialogContent>
                <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
                    {/* Subcategory Title Input */}
                    <Box sx={{ marginBottom: 2 }}>
                        <InputLabel sx={{ paddingBottom: -1 }} >Title</InputLabel>
                        <TextField
                            // label="Subcategory Title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                        />
                    </Box>

                    {/* Parent Category Selector */}
                    <Box>
                        <InputLabel sx={{ paddingBottom: 1 }}>Category</InputLabel>
                        <Select
                            value={formData.parentCategory}
                            name="parentCategory"
                            onChange={handleChange}
                            placeholder="Select a category"
                            fullWidth
                            style={{ height: 40 }}
                        >
                            <MenuItem value="Select a Category" disabled >
                                Select a Category
                            </MenuItem>
                            {categories.map((category) => (
                                <MenuItem key={category._id} value={category._id}>
                                    {category.catName}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleSave} style={{ color: "white" }} color="primary" variant="contained">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
